/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { cssMap, jsx } from '@compiled/react';

import { token } from '@atlaskit/tokens';

import type { CollabGraphRecommendedEntity } from '../../../../utils/collaborationUsers';

type CollaboratorProps = {
	user: CollabGraphRecommendedEntity;
	/**
	 * URL of the SVG mask that clips the collaborator's avatar into a
	 * rank-specific organic silhouette. The avatar is rendered through
	 * the white region of the mask.
	 */
	maskSrc: string;
	/**
	 * URL of the decorative background image painted behind the masked
	 * avatar. Each rank has its own background containing rank-specific
	 * decorative shapes baked into the image.
	 */
	backgroundSrc: string;
	/**
	 * Solid brand colour used as the card's background underneath the
	 * decorative `backgroundSrc` image. Acts as a fallback while the
	 * background image is loading and is also used to colour the rounded
	 * card outside of the avatar area.
	 */
	backgroundColor: string;
};

const styles = cssMap({
	card: {
		flex: '1 0 0',
		minWidth: 0,
		display: 'flex',
		flexDirection: 'column',
		alignItems: 'center',
		gap: token('space.200'),
		paddingBottom: token('space.500'),
		borderRadius: token('radius.small'),
		overflow: 'hidden',
		'@media (max-width: 30rem)': {
			width: '100%',
			flex: 'none',
		},
	},
	avatarWrapper: {
		position: 'relative',
		width: '100%',
		aspectRatio: '1 / 1',
		display: 'block',
	},
	background: {
		position: 'absolute',
		inset: 0,
		width: '100%',
		height: '100%',
		objectFit: 'cover',
		display: 'block',
	},
	avatar: {
		position: 'absolute',
		inset: 0,
		width: '100%',
		height: '100%',
		objectFit: 'cover',
		display: 'block',
		maskSize: 'cover',
		maskRepeat: 'no-repeat',
		maskPosition: 'center',
	},
	avatarPlaceholder: {
		position: 'absolute',
		inset: 0,
		// eslint-disable-next-line @atlaskit/design-system/ensure-design-token-usage
		backgroundColor: '#D9D9D9',
		maskSize: 'cover',
		maskRepeat: 'no-repeat',
		maskPosition: 'center',
	},
	name: {
		font: token('font.body'),
		fontFamily: token('font.family.code'),
		fontWeight: token('font.weight.semibold'),
		// eslint-disable-next-line @atlaskit/design-system/use-tokens-typography
		letterSpacing: '1px',
		// Dark text reads on every brand background colour used in this section
		// eslint-disable-next-line @atlaskit/design-system/ensure-design-token-usage
		color: '#111213',
		textAlign: 'center',
		// eslint-disable-next-line @atlaskit/design-system/use-tokens-typography
		textTransform: 'uppercase',
		whiteSpace: 'nowrap',
		margin: 0,
		alignSelf: 'stretch',
	},
});

/**
 * Renders a single collaborator card for the "Top 5" leaderboard.
 *
 * The card has a brand-coloured background filled with rank-specific
 * decorative shapes (`backgroundSrc`). The collaborator's avatar is
 * layered on top and clipped via a CSS mask (`maskSrc`) into a unique
 * organic silhouette per rank, so the decorative background shows
 * around it and the photo only appears within the masked region.
 */
export const Collaborator = ({
	user,
	maskSrc,
	backgroundSrc,
	backgroundColor,
}: CollaboratorProps): JSX.Element => {
	const { name, picture } = user.expand;

	// The avatar URL from the backend ends in `/128` (128px size). Request the
	// higher-resolution `/512` variant so the image stays crisp on larger and
	// high-DPI displays.
	const highResPicture = picture ? picture.replace(/\/128$/, '/512') : picture;
	const maskStyle = { maskImage: `url(${maskSrc})`, WebkitMaskImage: `url(${maskSrc})` };

	return (
		<div css={styles.card} style={{ backgroundColor }}>
			<div css={styles.avatarWrapper}>
				<img css={styles.background} src={backgroundSrc} alt="" aria-hidden="true" />
				{highResPicture ? (
					<img
						css={styles.avatar}
						src={highResPicture}
						alt={`${name}'s avatar`}
						aria-hidden="true"
						style={maskStyle}
					/>
				) : (
					<div css={styles.avatarPlaceholder} style={maskStyle} />
				)}
			</div>
			{/* eslint-disable-next-line @atlaskit/design-system/use-primitives-text */}
			<p css={styles.name}>{name}</p>
		</div>
	);
};
